import type { Dispatch, RefObject, SetStateAction } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHttpClient } from "../../../hooks/useHttpClient";
import { usePopup } from "../../../hooks/usePopup";
import type { User } from "../../../interfaces/user";
import { useLocalization } from "../../../stores/localizationContext";
import { useLiveSessionStore } from "../../../stores/useLiveSessionStore";
import { useUserContext } from "../../../stores/userContext";
import { Dropdown } from "../../Common/Dropdown/Dropdown";
import { ConfirmPopup } from "../../Popups/ConfirmPopup";
import { SlideTemplateWithCounter } from "./SlideTemplateWithCounter";
import GridIcon from "/src/assets/images/grid.svg";
import StreamIcon from "/src/assets/images/stream.svg";
import UndoRedoIcon from "/src/assets/images/undo-redo.svg";
// Define button types for better type safety
type ButtonType = "icon" | "svg";

// Define a proper type for buttons
interface ButtonConfig {
  type: ButtonType;
  class?: string;
  src?: string;
  alt?: string;
  label: string;
  extraClass?: string;
  ref?: RefObject<HTMLButtonElement | null>;
  dropdown?: string;
}

// Update the interface to include disabledButtons and slide data
interface SessionButtonsActionsProps {
  openDropdown: string;
  setOpenDropdown: Dispatch<SetStateAction<string>>;
  gridBtnRef: RefObject<HTMLButtonElement | null>;
  usersBtnRef: RefObject<HTMLButtonElement | null>;
  activeSlideIndex: number;
  screenData: Array<{
    id: string;
    slide_number: string;
    slide_path: string;
    slide_thumbnail_extension: string;
    type: string;
  }>;
  setIsSendingEngagement: Dispatch<SetStateAction<boolean>>; // Added prop
}

export const SessionButtonsActions = ({
  openDropdown,
  setOpenDropdown,
  gridBtnRef,
  usersBtnRef,
  activeSlideIndex,
  screenData,
  setIsSendingEngagement,
}: SessionButtonsActionsProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);


    const isVideoOn = useLiveSessionStore((state) => state.isVideoOn);
    const setIsVideoOn = useLiveSessionStore((state) => state.setIsVideoOn);
    const micStatus = useLiveSessionStore((state) => state.micStatus);
    const [disabledButtons, setDisabledButtons] = useState<string[]>([]);
    const joinedStudents = useLiveSessionStore((state) => state.joinedStudents);
    const removeStudent = useLiveSessionStore((state) => state.removeStudent);
    // ✅ Engine communication (side effect)
    const sendMessageToEngine = useLiveSessionStore((state) => state.sendMessageToEngine);
    const sessionData = useLiveSessionStore((state) => state.sessionData);
    const { user } = useUserContext();
    const api = useHttpClient();
    // Localization
    const { t } = useLocalization();

  // State for showing popup and tracking student to kick out
  const [showKickoutPopup, setShowKickoutPopup] = useState(false);
  const [studentToKickout, setStudentToKickout] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Handle kick out student
  const handleKickOutStudent = useCallback(async () => {
    if (!studentToKickout) return;

    try {
      let endpoint = import.meta.env.VITE_KICK_OUT_STUDENT;
      if (endpoint.includes("{session_id}"))
        endpoint = endpoint.replace(
          "{session_id}",
          sessionData?.sessionId || ""
        );
      if (endpoint.includes("{educator_id}"))
        endpoint = endpoint.replace("{educator_id}", user?.userId || "");
      if (endpoint.includes("{student_id}"))
        endpoint = endpoint.replace("{student_id}", studentToKickout.id);

      const response = await api.post(endpoint);

      if (response.status === 200) {
        removeStudent(studentToKickout.id);
        sendMessageToEngine("addMutualSessionActionLog", {
          resource: "session",
          action: "kick_out",
          receiver: {
            id: studentToKickout.id,
            name: studentToKickout.name,
            type: "student",
          },
        });

        setShowKickoutPopup(false);
        setStudentToKickout(null);
      }
    } catch (err) {
      console.error("Failed to kick out student:", err);
      // Keep popup open on error
    }
  }, [
    studentToKickout,
    sessionData?.sessionId,
    user?.userId,
    api,
    removeStudent,
    sendMessageToEngine,
  ]);

  // Memoize the popup content to prevent unnecessary re-renders
  const kickOutPopupContent = useMemo(() => {
    if (!showKickoutPopup) return null;

    return (
      <ConfirmPopup
        onConfirm={handleKickOutStudent}
        onCancel={() => setShowKickoutPopup(false)}
        icon="user-times-icon"
        title={t("remove_student_title")}
        message={t("kick_out_student_message")}
        confirmText={t("remove_button")}
        cancelText={t("cancel_label")}
        extraClass="kick-out-popup"
      />
    );
  }, [showKickoutPopup, handleKickOutStudent, t]);

  // Setup popup with KickoutPopup component
  const KickOutPopupComponent = usePopup(kickOutPopupContent);

  const openKickoutPopup = (student: User) => {
    setStudentToKickout({ id: student.userId, name: student.firstName || "" });
    setShowKickoutPopup(true);
  };

  // Define buttons outside of the render to improve readability
  const buttons: ButtonConfig[] = [
    { type: "icon", class: "plus-icon", label: "Add" },
    {
      type: "icon",
      class: "paper-plane-icon",
      label: "Send",
      // Removed extraClass as we'll wrap this button instead
    },
    {
      type: "svg",
      src: GridIcon,
      alt: "grid",
      label: "Grid",
      ref: gridBtnRef,
      dropdown: "grid",
    },
    {
      type: "svg",
      src: UndoRedoIcon,
      alt: "undo",
      extraClass: "undo-btn",
      label: "Undo",
    },
    {
      type: "svg",
      src: UndoRedoIcon,
      alt: "redo",
      extraClass: "redo-btn",
      label: "Redo",
    },
    { type: "icon", class: "sync-icon", label: "Sync" },
    { type: "icon", class: "video-icon", label: "Video" },
    { type: "svg", src: StreamIcon, alt: "stream", label: "Stream" },
    {
      type: "icon",
      class: "user-friends-icon",
      label: "Students",
      ref: usersBtnRef,
      dropdown: "users",
    },
  ];

  useEffect(() => {
    setDisabledButtons((prev) => {
      const filtered = prev.filter(
        (b) => b !== "video-icon" && b !== "paper-plane-icon"
      );

            // Add "video-icon" only if mic is NOT muted
            if (micStatus === "muted") {

                setIsVideoOn(false); //if mute mic, disable video
                filtered.push("video-icon");
            }

      if (screenData.length > 0 && activeSlideIndex < screenData.length) {
        const currentSlide = screenData[activeSlideIndex];
        if (currentSlide?.type === "instructional") {
          filtered.push("paper-plane-icon");
        }
      }

      return filtered;
    });
  }, [micStatus, screenData, activeSlideIndex, setIsVideoOn]); // Added missing dependency

  // Handle dropdown toggle in a separate function for cleaner code
  const handleDropdownToggle = (dropdownName: string) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown("");
    } else {
      setOpenDropdown(dropdownName);
    }
  };

  // Handle button click actions
  const handleButtonClick = (btn: ButtonConfig, isDropdown: boolean) => {
    if (btn.label === "Video") {
      setIsVideoOn(!isVideoOn);
      return;
    }

    if (btn.label === "Add") {
      console.log(
        "[SessionButtonsActions] Adding new blank slide with 25% width..."
      );
      // Add blank slide with 25% width requirement
      sendMessageToEngine("addBlankArea", {
        widthPercentage: 25, // 25% of slide width
        heightRatio: 1 / 3, // 1/3 of height as specified
      });
      return;
    }

    if (btn.label === "Send") {
      // Check if current slide is instructional - if so, don't send
      if (screenData.length > 0 && activeSlideIndex < screenData.length) {
        const currentSlide = screenData[activeSlideIndex];
        if (currentSlide?.type === "instructional") {
          console.log(
            "[SessionButtonsActions] Send button disabled for instructional slide"
          );
          return;
        }
      }

      // Send engagement logic
      console.log("[SessionButtonsActions] Sending engagement...");
      sendMessageToEngine("sendEngagement", {}); // Notify engine
      setIsSendingEngagement(true);
      return;
    }

    if (btn.label === "Undo") {
      console.log(
        "[SessionButtonsActions] Calling NagwaRenderingEngine.undo()"
      );
      sendMessageToEngine("undo", {});
      return;
    }

    if (btn.label === "Redo") {
      console.log(
        "[SessionButtonsActions] Calling NagwaRenderingEngine.redo()"
      );
      sendMessageToEngine("redo", {});
      return;
    }

    if (isDropdown && btn.dropdown) {
      handleDropdownToggle(btn.dropdown);
    }
  };

  // Compute the active class(es) for a button based on state
  const isActiveButton = (btn: ButtonConfig, isDropdown: boolean): boolean => {
    if (
      (isDropdown && btn.dropdown && openDropdown === btn.dropdown) ||
      (btn.label === "Video" && isVideoOn)
    ) {
      return true;
    }

    return false;
  };

  return (
    <div className="toolbar__actions session-buttons-actions">
      {buttons.map((btn, idx) => {
        const isIcon = btn.type === "icon";
        const isDropdown = !!btn.dropdown;
        // Use optional chaining for cleaner null checking
        const isDisabled = !!(
          isIcon &&
          btn.class &&
          disabledButtons.includes(btn.class)
        );

        // Check if this is the paper-plane-icon button to wrap it
        // const isPaperPlaneBtn = isIcon && btn.class === "paper-plane-icon";


                return (
                    <React.Fragment key={btn.class || btn.alt || idx}>
                        <button
                            ref={btn.ref}
                            className={`btn app-btn-icon btn-white${btn.extraClass ? ` ${btn.extraClass}` : ""
                                }${isActiveButton(btn, isDropdown) ? " active" : ""}`}
                            aria-label={btn.label}
                            onClick={() => handleButtonClick(btn, isDropdown)}
                            disabled={isDisabled}
                        >
                            {isIcon ? (
                                <i className={`f-icon fa-light ${btn.class}`}></i>
                            ) : (
                                <img src={btn.src} alt={btn.alt} />
                            )}
                        </button>
                        {isDropdown && btn.dropdown === "grid" && (
                            <Dropdown
                                open={openDropdown === "grid"}
                                anchorRef={gridBtnRef}
                                onClose={() => setOpenDropdown("")}
                                className="grid-dropdown dropdown-menu"
                            >
                                <h4 className="dropdown-title">Choose a Background</h4>
                                <ul>
                                    {[
                                        "blank",
                                        "slide-templete-1.svg",
                                        "slide-templete-2.svg",
                                    ].map((img, i) => (
                                        <li key={img}>
                                            {img === "blank" ? (
                                                <div
                                                    className={`btn slide-template${selectedTemplate === "blank" ? " selected" : ""
                                                        }`}
                                                    onClick={() => setSelectedTemplate("blank")}
                                                >
                                                    <div className="blank-template"></div>
                                                </div>
                                            ) : (
                                                <SlideTemplateWithCounter 
                                                    src={`/assets/images/${img}`}
                                                    alt={`slide-templete ${i + 1}`}
                                                    onClick={() => setSelectedTemplate(img)}
                                                    isSelected={selectedTemplate === img}
                                                />
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                        )}
                        {isDropdown && btn.dropdown === "users" && (
                            <Dropdown
                                open={openDropdown === "users"}
                                anchorRef={usersBtnRef}
                                onClose={() => setOpenDropdown("")}
                                className="users-dropdown"
                                offsetX={-170}
                            >
                                <h4 className="dropdown-title">{t("students_lable")}</h4>
                                {(joinedStudents.length == 0) ? (
                                    <div className="empty-state">
                                        <p>{t("no_students_message")}</p>
                                    </div>
                                ) : (
                                    <ul className="dropdown-menu">
                                        {joinedStudents.map((student, i) => (
                                            <li key={i}>
                                                <span> {student.firstName}  </span>
                                                <button 
                                                    className="btn btn-link" 
                                                    onClick={() => openKickoutPopup(student)}
                                                >
                                                    <i className="f-icon user-times-icon fa-medium"></i>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Dropdown>
                        )}
                    </React.Fragment>
                );
            })}

      <KickOutPopupComponent />
    </div>
  );
};
